import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Badge, Table, Modal } from 'react-bootstrap';
import axios from 'axios';

interface UpgradeRequest {
  id: string;
  transactionId: string;
  amountPaid: number;
  paymentMethod: string;
  paymentDate: string;
  status: 'pending' | 'approved' | 'rejected';
  adminResponse: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: {
    name: string;
    email: string;
  };
  notes: string;
}

interface UserStatus {
  id: string;
  name: string;
  email: string;
  userType: 'free' | 'executive' | 'admin';
  lastUpgradeDate?: string;
  canUpgrade: boolean;
}

const UpgradePage: React.FC = () => {
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [requests, setRequests] = useState<UpgradeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);

  const [formData, setFormData] = useState({
    transactionId: '',
    amountPaid: 1000, // Default executive plan price
    paymentMethod: 'upi' as 'bank_transfer' | 'upi' | 'credit_card' | 'debit_card' | 'net_banking' | 'other',
    paymentDate: new Date().toISOString().split('T')[0],
    accountNumber: '',
    accountName: '',
    bankIFSC: '',
    notes: ''
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  const paymentMethods = [
    { value: 'upi', label: 'UPI (Google Pay, PhonePe, Paytm)' },
    { value: 'bank_transfer', label: 'Bank Transfer/NEFT/RTGS' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'net_banking', label: 'Net Banking' },
    { value: 'other', label: 'Other' }
  ];

  const executiveFeatures = [
    'Unlimited task submissions',
    'Priority access to high-value tasks',
    'Priority customer support', 
    'Enhanced referral tracking',
    'Exclusive executive-only tasks',
    'Dedicated customer support',
    'Early access to new features'
  ];

  useEffect(() => {
    loadUserStatus();
    loadUpgradeRequests();
    
    // Set up polling to check for status updates every 10 seconds
    const statusCheckInterval = setInterval(() => {
      loadUserStatus();
      loadUpgradeRequests();
    }, 10000);
    
    // Cleanup interval on unmount
    return () => clearInterval(statusCheckInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/upgrade/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserStatus(response.data.user);
      }
    } catch (error: any) {
      console.error('Error loading user status:', error);
      setError('Failed to load user status');
    } finally {
      setLoading(false);
    }
  };

  const loadUpgradeRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/upgrade/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setRequests(response.data.requests);
      }
    } catch (error: any) {
      console.error('Error loading upgrade requests:', error);
    }
  };

  const handleSubmitUpgrade = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/upgrade/request`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('Upgrade request submitted successfully! Please wait for admin approval.');
        setShowModal(false);
        setFormData({
          transactionId: '',
          amountPaid: 1000,
          paymentMethod: 'upi',
          paymentDate: new Date().toISOString().split('T')[0],
          accountNumber: '',
          accountName: '',
          bankIFSC: '',
          notes: ''
        });
        // Reload data
        loadUserStatus();
        loadUpgradeRequests();
      }
    } catch (error: any) {
      console.error('Error submitting upgrade request:', error);
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) => 
          `${err.path ? err.path.charAt(0).toUpperCase() + err.path.slice(1) : ''}: ${err.msg}`
        );
        setError(`Validation failed:\n• ${errorMessages.join('\n• ')}`);
      } else {
        setError(error.response?.data?.message || 'Failed to submit upgrade request');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  const getUserTypeBadge = (userType: string) => {
    const variants: { [key: string]: string } = {
      free: 'secondary',
      executive: 'warning',
      admin: 'danger'
    };
    const type = userType || 'free';
    return <Badge bg={variants[type] || 'secondary'}>{type.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin fa-2x mb-3"></i>
          <p>Loading your upgrade status...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row>
        <Col lg={8} className="mx-auto">
          {/* Header */}
          <div className="text-center mb-5">
            <h1 className="display-5 fw-bold">
              <i className="fas fa-rocket text-primary me-3"></i>
              {userStatus?.userType === 'executive' || userStatus?.userType === 'admin' ? 'Account Status' : 'Upgrade to Executive'}
            </h1>
            <p className="lead text-muted">
              {userStatus?.userType === 'executive' || userStatus?.userType === 'admin' 
                ? 'Manage your premium account' 
                : 'Unlock premium features and earn more rewards'}
            </p>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => {
                loadUserStatus();
                loadUpgradeRequests();
              }}
              className="mt-2"
            >
              <i className="fas fa-sync-alt me-2"></i>
              Refresh Status
            </Button>
          </div>

          {/* Alerts */}
          {error && (
            <Alert variant="danger" className="mb-4">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" className="mb-4">
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          )}

          {/* Current Status */}
          {userStatus && (
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Body>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="mb-2">
                      <i className="fas fa-user me-2"></i>
                      Current Plan: {getUserTypeBadge(userStatus.userType)}
                    </h5>
                    <p className="text-muted mb-0">
                      Account: {userStatus.name} ({userStatus.email})
                    </p>
                    {userStatus.lastUpgradeDate && (
                      <small className="text-muted">
                        Last upgraded: {new Date(userStatus.lastUpgradeDate).toLocaleDateString()}
                      </small>
                    )}
                  </Col>
                  <Col md={4} className="text-end">
                    {userStatus.canUpgrade ? (
                      <Button 
                        variant="primary" 
                        size="lg"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="fas fa-arrow-up me-2"></i>
                        Upgrade Now
                      </Button>
                    ) : (
                      <p className="text-muted mb-0">
                        {userStatus.userType === 'executive' ? 'You already have Executive access' : 
                         userStatus.userType === 'admin' ? 'You have Admin access' : 
                         'Upgrade request pending'}
                      </p>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Executive Features - Only show if user is not executive/admin */}
          {userStatus?.userType !== 'executive' && userStatus?.userType !== 'admin' && (
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-crown me-2"></i>
                  Executive Plan Features
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      {executiveFeatures.slice(0, Math.ceil(executiveFeatures.length / 2)).map((feature, index) => (
                        <li key={index} className="mb-2">
                          <i className="fas fa-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      {executiveFeatures.slice(Math.ceil(executiveFeatures.length / 2)).map((feature, index) => (
                        <li key={index} className="mb-2">
                          <i className="fas fa-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Col>
                </Row>
                <div className="text-center mt-4">
                  <h3 className="text-primary">₹1000/per year</h3>
                  <p className="text-muted">Annual subscription for full access</p>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Executive Features Summary for existing executive/admin users */}
          {(userStatus?.userType === 'executive' || userStatus?.userType === 'admin') && (
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header className="bg-success text-white">
                <h5 className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  Your Active Executive Benefits
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      {executiveFeatures.slice(0, Math.ceil(executiveFeatures.length / 2)).map((feature, index) => (
                        <li key={index} className="mb-2">
                          <i className="fas fa-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Col>
                  <Col md={6}>
                    <ul className="list-unstyled">
                      {executiveFeatures.slice(Math.ceil(executiveFeatures.length / 2)).map((feature, index) => (
                        <li key={index} className="mb-2">
                          <i className="fas fa-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </Col>
                </Row>
                <div className="text-center mt-4">
                  <div className="alert alert-success mb-0">
                    <i className="fas fa-crown me-2"></i>
                    <strong>You're enjoying all Executive benefits!</strong>
                    {userStatus?.lastUpgradeDate && (
                      <div className="small mt-1">
                        Executive since: {new Date(userStatus.lastUpgradeDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Upgrade Requests History */}
          {requests.length > 0 && (
            <Card className="mb-4 border-0 shadow-sm">
              <Card.Header>
                <h5 className="mb-0">
                  <i className="fas fa-history me-2"></i>
                  Your Upgrade Requests
                </h5>
              </Card.Header>
              <Card.Body>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map(request => (
                      <tr key={request.id}>
                        <td>
                          <code>{request.transactionId}</code>
                        </td>
                        <td>₹{request.amountPaid}</td>
                        <td>{getStatusBadge(request.status)}</td>
                        <td>
                          <small>{new Date(request.submittedAt).toLocaleString()}</small>
                        </td>
                        <td>
                          {request.adminResponse && (
                            <small className="text-muted">{request.adminResponse}</small>
                          )}
                          {request.status === 'pending' && (
                            <small className="text-warning">Under review</small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Upgrade Request Modal - Only show for non-executive users */}
      {userStatus?.userType !== 'executive' && userStatus?.userType !== 'admin' && (
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-credit-card me-2"></i>
              Submit Upgrade Request
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmitUpgrade}>
            <Modal.Body>
            <Alert variant="info" className="mb-4">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Payment Instructions:</strong><br />
              1. Make annual payment of ₹1000 to our account<br />
              2. Note down the transaction ID<br />
              3. Fill the form below with payment details<br />
              4. System will verify and approve your request<br /><br />
              
              <strong>Please find the Bank Account details:</strong><br />
              <strong>Account Number:</strong> 1658104000067634<br />
              <strong>Bank Name:</strong> IDBI Bank<br />
              <strong>A/c Holder Name:</strong> NARENDER KAUR<br />
              <strong>IFSC Code:</strong> IBKL0001658
            </Alert>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Transaction ID *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.transactionId}
                    onChange={(e) => setFormData(prev => ({...prev, transactionId: e.target.value}))}
                    placeholder="Enter transaction/reference ID"
                    required
                  />
                  <Form.Text className="text-muted">
                    Unique ID from your payment receipt
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount Paid *</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.amountPaid}
                    onChange={(e) => setFormData(prev => ({...prev, amountPaid: parseFloat(e.target.value) || 0}))}
                    min="1"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method *</Form.Label>
                  <Form.Select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData(prev => ({...prev, paymentMethod: e.target.value as any}))}
                    required
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.paymentDate}
                    onChange={(e) => setFormData(prev => ({...prev, paymentDate: e.target.value}))}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Number *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData(prev => ({...prev, accountNumber: e.target.value}))}
                    placeholder="Enter your bank account number"
                    required
                  />
                  <Form.Text className="text-muted">
                    Account number from which payment was made
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Account Holder Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.accountName}
                    onChange={(e) => setFormData(prev => ({...prev, accountName: e.target.value}))}
                    placeholder="Enter account holder name"
                    required
                  />
                  <Form.Text className="text-muted">
                    Name as per bank account
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Bank IFSC Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.bankIFSC}
                    onChange={(e) => setFormData(prev => ({...prev, bankIFSC: e.target.value.toUpperCase()}))}
                    placeholder="e.g., HDFC0001234"
                    style={{ textTransform: 'uppercase' }}
                    pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                    maxLength={11}
                    required
                  />
                  <Form.Text className="text-muted">
                    Format: BANK0123456 (First 4 letters = bank code, 5th character = 0, last 6 = branch code)
                  </Form.Text>
                  {formData.bankIFSC && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bankIFSC) && (
                    <Form.Text className="text-danger">
                      Invalid IFSC format. Should be 11 characters: BANK0123456
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Additional Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                placeholder="Any additional information about the payment..."
                maxLength={500}
              />
              <Form.Text className="text-muted">
                {formData.notes.length}/500 characters
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowModal(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  Submit Request
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      )}
    </Container>
  );
};

export default UpgradePage;